import { EditOutline24Icon } from "@vygruppen/spor-icon-react";
import {
  AffectedTrain,
  AffectedTrain as AffectedTrainType,
} from "features/CenterContent/RoleContent/Vaktleder/types";
import { EditTrainInfoModal } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/EditTrainInfoModal";
import { TrainInfoState } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import DashedLine from "features/CenterContent/VehicleDetails/shared/DashedLine";
import { VStackContainer } from "features/VehicleSidebar/AffectedTrains/styles";
import {
  getEventSemantics,
  getInfoTitle,
} from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  DropsBadge,
  Button,
  Field,
  Flex,
  Grid,
  HStack,
  Skeleton,
  Spacer,
  Stack,
} from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { InfrastructureIncidentIcon18 } from "shared/icons/InfrastructureIncidentIcon18";
import styled, { useTheme } from "styled-components";

type HandledTrainListElementProps = {
  train: AffectedTrainType;
  selectedTrainIds: AffectedTrain[];
  selectTrain: (train: AffectedTrain) => void;
  loading: boolean;
};

const LocalVStackWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  gap: 6px;
  grid-template-areas:
    "trainId"
    "stations"
    "affected"
    "badge";
  align-items: center;
`;

const HandledTrainListElement: FC<HandledTrainListElementProps> = ({
  train,
  selectedTrainIds,
  selectTrain,
  loading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const trainInfo = train.referencedOperationalTrainInformation.at(0);
  const newestVersion = trainInfo?.versions.at(0);
  const newestVersionopen = newestVersion?.state === TrainInfoState.OPEN;

  return (
    <>
      <Stack as="li" w="100%" gap={0}>
        <Grid
          autoFlow="column"
          autoColumns="min-content 1.5fr 1fr"
          p="12px"
          columnGap={0}
          backgroundColor={theme.colorBackgroundSecondary}
          borderRadius={6}
        >
          {newestVersionopen && !loading ? (
            <Field>
              <Checkbox
                pr="18px"
                checked={selectedTrainIds.includes(train)}
                name="Select Train"
                onCheckedChange={() => selectTrain(train)}
              />
            </Field>
          ) : (
            <Spacer width="42px" />
          )}

          <VStackContainer>
            <LocalVStackWrap>
              <Text style={{ gridArea: "trainId" }} bold>
                {train.trainId.identifier}
              </Text>

              <Text style={{ gridArea: "stations" }}>
                {train.origin}–{train.destination}
              </Text>

              <HStack gridArea="affected">
                <InfrastructureIncidentIcon18 />
                <Text>{train.eventStretchName}</Text>
              </HStack>

              <HStack style={{ gridArea: "badge" }} alignItems="flex-start">
                {train.referencedOperationalTrainInformation
                  .filter((info) => info.versions.length > 0)
                  .map((info) => {
                    const version = info.versions[0];
                    const isClosed = version.state !== TrainInfoState.OPEN;
                    return loading ? (
                      <Skeleton
                        key={info.latestInfoUuid}
                        height="24px"
                        width="100px"
                        borderRadius="16px"
                      />
                    ) : (
                      <Flex flexDir="row" gap="6px" key={info.latestInfoUuid}>
                        <DropsBadge
                          semantics={
                            isClosed ? "info" : getEventSemantics(version.type)
                          }
                          key={info.latestInfoUuid}
                          whiteSpace="pre"
                        >
                          {getInfoTitle(version, t)}
                        </DropsBadge>
                        {isClosed && (
                          <DropsBadge
                            semantics="success"
                            key={info.latestInfoUuid}
                          >
                            {t("Friskmeldt")}
                          </DropsBadge>
                        )}
                      </Flex>
                    );
                  })}
              </HStack>
            </LocalVStackWrap>
          </VStackContainer>

          <Flex alignSelf="flex-start" width="fit-content" mr={0} ml="auto">
            {loading && (
              <Skeleton height="36px" width="120px" m="auto" borderRadius={5} />
            )}

            {!loading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setModalOpen(true)}
                leftIcon={<EditOutline24Icon />}
                mr={0}
                ml="auto"
              >
                {newestVersionopen ? t("Endre") : t("Gjenåpne")}
              </Button>
            )}
          </Flex>
        </Grid>
        <DashedLine variant="secondary" />
      </Stack>
      {modalOpen && trainInfo && (
        <EditTrainInfoModal
          uuid={trainInfo.latestInfoUuid}
          trainId={train.trainId.identifier}
          nominalDate={train.trainId.nominalDate}
          countryCode={train.trainId.countryCode}
          setModalOpen={setModalOpen}
          trainInfoWithOpenState={[]}
          title={newestVersionopen ? undefined : t("Gjenåpne hendelse")}
          submitButtonLabel={newestVersionopen ? undefined : t("Gjenåpne")}
        />
      )}
    </>
  );
};

export default HandledTrainListElement;
